import { Card, Descriptions, Tag, Space, Progress, Typography, Row, Col, Statistic } from 'antd';
import { InboxOutlined, ColumnHeightOutlined, ExpandOutlined, SafetyCertificateOutlined } from '@ant-design/icons';

const { Text } = Typography;

interface CargoTrip {
  id: number;
  cargoDescription: string;
  cargoType: 'GENERAL' | 'FRAGILE' | 'PERISHABLE' | 'HAZARDOUS' | 'LIVESTOCK' | 'VEHICLE';
  weight: number;
  weightUnit: 'KG' | 'TON';
  length?: number;
  width?: number;
  height?: number;
  volume?: number;
  requiresRefrigeration: boolean;
  temperatureMin?: number;
  temperatureMax?: number;
  requiresSpecialHandling: boolean;
  handlingInstructions?: string;
  insuranceRequired: boolean;
  insuranceValue?: number;
  loadingAssistance: boolean;
  unloadingAssistance: boolean;
  vehicleType: 'PICKUP' | 'VAN' | 'TRUCK_SMALL' | 'TRUCK_MEDIUM' | 'TRUCK_LARGE' | 'FLATBED' | 'REFRIGERATED';
}

interface CargoTripDetailsProps {
  trip: CargoTrip;
}

const cargoTypeLabels: Record<string, string> = {
  GENERAL: 'Umumiy yuk',
  FRAGILE: 'Mo\'rt yuk',
  PERISHABLE: 'Tez buziluvchi',
  HAZARDOUS: 'Xavfli yuk',
  LIVESTOCK: 'Tirik hayvonlar',
  VEHICLE: 'Avtomobil',
};

const cargoTypeColors: Record<string, string> = {
  GENERAL: 'default',
  FRAGILE: 'orange',
  PERISHABLE: 'cyan',
  HAZARDOUS: 'red',
  LIVESTOCK: 'green',
  VEHICLE: 'blue',
};

const vehicleTypeLabels: Record<string, string> = {
  PICKUP: 'Pikap',
  VAN: 'Furgon',
  TRUCK_SMALL: 'Kichik yuk mashinasi',
  TRUCK_MEDIUM: 'O\'rta yuk mashinasi',
  TRUCK_LARGE: 'Katta yuk mashinasi',
  FLATBED: 'Platformali',
  REFRIGERATED: 'Sovutgichli',
};

export const CargoTripDetails = ({ trip }: CargoTripDetailsProps) => {
  const formatWeight = () => {
    if (trip.weightUnit === 'TON') {
      return `${trip.weight} tonna`;
    }
    return `${trip.weight} kg`;
  };

  const formatDimensions = () => {
    if (!trip.length && !trip.width && !trip.height) return '-';
    const parts = [];
    if (trip.length) parts.push(`${trip.length} m`);
    if (trip.width) parts.push(`${trip.width} m`);
    if (trip.height) parts.push(`${trip.height} m`);
    return parts.join(' x ');
  };

  const formatVolume = () => {
    if (trip.volume) return `${trip.volume} m³`;
    if (trip.length && trip.width && trip.height) {
      const vol = trip.length * trip.width * trip.height;
      return `${vol.toFixed(2)} m³`;
    }
    return '-';
  };

  return (
    <>
      {/* Cargo Type & Weight */}
      <Card
        title={
          <Space>
            <InboxOutlined />
            <span>Yuk ma'lumotlari</span>
          </Space>
        }
        style={{ marginTop: 24 }}
      >
        <Row gutter={[24, 24]}>
          <Col xs={24} sm={8}>
            <Statistic
              title="Yuk turi"
              value={cargoTypeLabels[trip.cargoType] || trip.cargoType}
              prefix={
                <Tag color={cargoTypeColors[trip.cargoType]} style={{ marginRight: 8 }}>
                  {cargoTypeLabels[trip.cargoType]?.charAt(0) || 'Y'}
                </Tag>
              }
              valueStyle={{ fontSize: 16 }}
            />
          </Col>
          <Col xs={24} sm={8}>
            <Statistic
              title="Vazn"
              value={formatWeight()}
              prefix={<ColumnHeightOutlined style={{ color: '#1ABC9C' }} />}
            />
          </Col>
          <Col xs={24} sm={8}>
            <Statistic
              title="Hajm"
              value={formatVolume()}
              prefix={<ExpandOutlined style={{ color: '#3498DB' }} />}
            />
          </Col>
        </Row>

        <Descriptions column={2} style={{ marginTop: 24 }}>
          <Descriptions.Item label="Tavsif" span={2}>
            {trip.cargoDescription || '-'}
          </Descriptions.Item>
          <Descriptions.Item label="O'lchamlar (U x K x B)">
            {formatDimensions()}
          </Descriptions.Item>
          <Descriptions.Item label="Transport turi">
            <Tag color="blue">{vehicleTypeLabels[trip.vehicleType] || trip.vehicleType}</Tag>
          </Descriptions.Item>
        </Descriptions>
      </Card>

      {/* Special Requirements */}
      <Card
        title={
          <Space>
            <SafetyCertificateOutlined />
            <span>Maxsus talablar</span>
          </Space>
        }
        style={{ marginTop: 24 }}
      >
        <Row gutter={[16, 16]}>
          <Col xs={12} sm={6}>
            <Card size="small" bodyStyle={{ textAlign: 'center', padding: 16 }}>
              <div style={{ marginBottom: 8 }}>
                {trip.requiresRefrigeration ? (
                  <Tag color="cyan">Talab qilinadi</Tag>
                ) : (
                  <Tag>Yo'q</Tag>
                )}
              </div>
              <Text type="secondary">Sovutish</Text>
            </Card>
          </Col>
          <Col xs={12} sm={6}>
            <Card size="small" bodyStyle={{ textAlign: 'center', padding: 16 }}>
              <div style={{ marginBottom: 8 }}>
                {trip.requiresSpecialHandling ? (
                  <Tag color="orange">Talab qilinadi</Tag>
                ) : (
                  <Tag>Yo'q</Tag>
                )}
              </div>
              <Text type="secondary">Maxsus ishlov</Text>
            </Card>
          </Col>
          <Col xs={12} sm={6}>
            <Card size="small" bodyStyle={{ textAlign: 'center', padding: 16 }}>
              <div style={{ marginBottom: 8 }}>
                {trip.loadingAssistance ? (
                  <Tag color="green">Ha</Tag>
                ) : (
                  <Tag>Yo'q</Tag>
                )}
              </div>
              <Text type="secondary">Yuklash yordami</Text>
            </Card>
          </Col>
          <Col xs={12} sm={6}>
            <Card size="small" bodyStyle={{ textAlign: 'center', padding: 16 }}>
              <div style={{ marginBottom: 8 }}>
                {trip.unloadingAssistance ? (
                  <Tag color="green">Ha</Tag>
                ) : (
                  <Tag>Yo'q</Tag>
                )}
              </div>
              <Text type="secondary">Tushirish yordami</Text>
            </Card>
          </Col>
        </Row>

        {/* Temperature Requirements */}
        {trip.requiresRefrigeration && (trip.temperatureMin !== undefined || trip.temperatureMax !== undefined) && (
          <Card size="small" style={{ marginTop: 16 }}>
            <Descriptions column={2}>
              <Descriptions.Item label="Minimal harorat">
                {trip.temperatureMin !== undefined ? `${trip.temperatureMin}°C` : '-'}
              </Descriptions.Item>
              <Descriptions.Item label="Maksimal harorat">
                {trip.temperatureMax !== undefined ? `${trip.temperatureMax}°C` : '-'}
              </Descriptions.Item>
            </Descriptions>
          </Card>
        )}

        {/* Handling Instructions */}
        {trip.handlingInstructions && (
          <Card size="small" style={{ marginTop: 16 }}>
            <Text strong>Ishlov berish ko'rsatmalari:</Text>
            <p style={{ marginTop: 8, color: '#7F8C8D' }}>
              {trip.handlingInstructions}
            </p>
          </Card>
        )}
      </Card>

      {/* Insurance */}
      {trip.insuranceRequired && (
        <Card
          title={
            <Space>
              <SafetyCertificateOutlined />
              <span>Sug'urta</span>
              <Tag color="green">Faol</Tag>
            </Space>
          }
          style={{ marginTop: 24 }}
        >
          <Descriptions column={1}>
            <Descriptions.Item label="Sug'urta qiymati">
              {trip.insuranceValue
                ? new Intl.NumberFormat('uz-UZ').format(trip.insuranceValue) + ' so\'m'
                : 'Ko\'rsatilmagan'}
            </Descriptions.Item>
          </Descriptions>
        </Card>
      )}
    </>
  );
};
